import React from 'react';
import { T, money } from '../customer/customers.theme';
import { SERIES } from './dashboard.theme';
import { BarRow, LegendRow, Panel, SplitBar } from './DashboardUI';

/**
 * The three breakdowns, and the weekday panel.
 *
 * Each one answers a different question about the same money, so none of them
 * is a chart for its own sake: how the orders arrived, whether the money is in
 * the drawer, and what kind of food it came from.
 */

/* ── how orders came in ─────────────────────────────────────────────────── */

export function OrderTypePanel({ rows, orders, currency }) {
  return (
    <Panel
      title="How orders came in"
      subtitle={`${orders} ${orders === 1 ? 'order' : 'orders'}`}
    >
      <SplitBar parts={rows.map((row) => ({ ...row, key: row.key }))} colors={SERIES} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, paddingTop: 16 }}>
        {rows.map((row, index) => (
          <LegendRow
            key={row.key}
            color={SERIES[index % SERIES.length]}
            label={row.label}
            count={row.count}
            value={money(row.value, currency)}
          />
        ))}
      </div>
    </Panel>
  );
}

/* ── how it was paid ────────────────────────────────────────────────────── */

/**
 * Three buckets, because two would lie.
 *
 * "Cash vs invoice" reads as "in the drawer vs on the book", but an order can
 * be marked cash and still be sitting unpaid on a table — counting it as cash
 * overstates the till by exactly the amount nobody has collected. So what is
 * open gets its own segment, and the three add up to the period's revenue.
 */
export function PaymentPanel({ rows, currency, note }) {
  return (
    <Panel title="How it was paid" subtitle="Collected, still open, or on a customer's balance">
      <SplitBar parts={rows} colors={[SERIES[2], T.pendingInk, SERIES[1]]} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, paddingTop: 16 }}>
        {rows.map((row, index) => (
          <LegendRow
            key={row.key}
            color={[SERIES[2], T.pendingInk, SERIES[1]][index]}
            label={row.label}
            count={row.count}
            value={money(row.value, currency)}
          />
        ))}
      </div>
      {note ? (
        <div style={{ paddingTop: 12, fontSize: 11.5, color: T.inkFaint, lineHeight: 1.5 }}>
          {note}
        </div>
      ) : null}
    </Panel>
  );
}

/* ── what kind of food ──────────────────────────────────────────────────── */

/**
 * One colour for every bar.
 *
 * Categories have no natural order, so shading them by size would double-encode
 * the length the bar already shows and burn the only channel left. The one
 * exception is "No category", drawn grey because it is not a category — it is
 * lines whose menu item has been deleted since.
 */
export function CategoryPanel({ rows, currency, limit = 5 }) {
  const shown = rows.slice(0, limit);
  const top = shown.reduce((max, row) => Math.max(max, row.revenue), 0);

  return (
    <Panel title="What kind of food" subtitle="Revenue by category">
      {shown.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          {shown.map((row) => (
            <BarRow
              key={row._id || 'none'}
              label={row.name}
              muted={!row._id}
              share={top > 0 ? (row.revenue / top) * 100 : 0}
              value={money(row.revenue, currency)}
            />
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 12.5, color: T.inkFaint }}>Nothing sold yet.</span>
      )}
    </Panel>
  );
}

/* ── which days are good ────────────────────────────────────────────────── */

/**
 * Only shown once the period covers more than one day — a weekday average
 * built from a single Tuesday is a Tuesday, dressed up.
 */
export function WeekdayPanel({ rows, currency, note }) {
  const top = rows.reduce((max, row) => Math.max(max, row.average), 0);

  return (
    <Panel title="Which days are good" subtitle="Average takings by weekday">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((row) => (
          <BarRow
            key={row.label}
            label={row.label}
            labelWidth={34}
            muted={!row.days}
            share={top > 0 ? (row.average / top) * 100 : 0}
            value={row.days ? money(row.average, currency) : '—'}
          />
        ))}
      </div>
      {note ? (
        <div style={{ paddingTop: 14, fontSize: 11.5, color: T.inkFaint, lineHeight: 1.5 }}>
          {note}
        </div>
      ) : null}
    </Panel>
  );
}
